/* FurCommand series PDP. Scoped enhancement; Shopify remains the source of truth.
 * Ajax Cart API: https://shopify.dev/docs/api/ajax/reference/cart
 * No checkout interception, client-side cart, mutation retries or price arithmetic.
 */
(() => {
  'use strict';
  const KEY = '__fcPdpSeriesControllerV1';
  if (window[KEY]) { window[KEY].mount(document); return; }
  const runtime = { instances: new Map(), queue: Promise.resolve(), locks: new Set(), scroll: null };
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const text = (root, selector, value) => all(root, selector).forEach(node => { node.textContent = value == null ? '' : String(value); });
  const safeNumber = (value, fallback) => Number.isSafeInteger(Number(value)) ? Number(value) : fallback;
  const errorMessage = data => {
    if (typeof data?.description === 'string') return data.description;
    if (typeof data?.message === 'string') return data.message;
    if (typeof data?.errors === 'string') return data.errors;
    return 'Shopify could not complete this cart request.';
  };

  function lockScroll(dialog) {
    if (runtime.locks.has(dialog)) return;
    if (!runtime.locks.size) {
      const body = document.body;
      runtime.scroll = { x: window.scrollX, y: window.scrollY, position: body.style.position, top: body.style.top,
        left: body.style.left, width: body.style.width, overflow: body.style.overflow };
      Object.assign(body.style, { position: 'fixed', top: `${-runtime.scroll.y}px`, left: `${-runtime.scroll.x}px`, width: '100%', overflow: 'hidden' });
    }
    runtime.locks.add(dialog);
  }
  function unlockScroll(dialog) {
    runtime.locks.delete(dialog);
    if (runtime.locks.size || !runtime.scroll) return;
    const saved = runtime.scroll;
    runtime.scroll = null;
    Object.assign(document.body.style, { position: saved.position, top: saved.top, left: saved.left, width: saved.width, overflow: saved.overflow });
    window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
  }

  class PDPController {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.destroyed = false;
      this.busy = false;
      this.cartReady = false;
      this.returnFocus = new Map();
      this.disabledBeforeBusy = new Map();
      this.product = JSON.parse(root.querySelector('[data-fc-product-data]').textContent);
      this.variants = new Map(this.product.variants.map(variant => [String(variant.id), variant]));
      this.form = root.querySelector('[data-fc-product-form]');
      this.cartDialog = root.querySelector('[data-fc-cart-dialog]');
      this.zoomDialog = root.querySelector('[data-fc-zoom-dialog]');
      this.cartContent = root.querySelector('[data-fc-cart-content]');
      this.sectionId = root.dataset.cartSection || 'fc-pdp-cart-content';
      this.media = all(root, '[data-fc-gallery-media]');
      this.thumbs = all(root, '[data-fc-gallery-thumb]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileVideo = window.matchMedia('(max-width: 750px)');
      const routeRoot = window.Shopify?.routes?.root || '/';
      this.urls = {
        add: this.localURL(root.dataset.cartAddUrl || `${routeRoot}cart/add.js`),
        change: this.localURL(root.dataset.cartChangeUrl || `${routeRoot}cart/change.js`),
        cart: this.localURL(root.dataset.cartUrl || `${routeRoot}cart.js`)
      };
      this.quantity = this.normalizeQuantity(root.querySelector('[data-fc-quantity]')?.value || 1);
      this.selected = this.variants.get(String(root.dataset.selectedVariant)) || this.variants.values().next().value;
      if (!this.selected || !this.form) throw new Error('Missing product variant or native form.');
      this.enhancedCart = Boolean(this.cartDialog?.showModal && this.cartContent && window.fetch);
      this.bind();
      this.selectVariant(this.selected.id, { announce: false, updateURL: false });
      if (!this.media.some(panel => !panel.hidden) && this.media.length) this.selectMedia(this.media[0].dataset.mediaId, false);
      this.respectReducedMotion();
      this.updateVideoPosters();
      this.observeRail();
      root.dataset.fcEnhanced = 'true';
    }

    localURL(path) {
      const url = new URL(path, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Cart routes must be same-origin.');
      return url.pathname + url.search;
    }
    on(target, type, handler, options = {}) {
      target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
    }
    bind() {
      this.on(this.root, 'click', event => this.click(event));
      this.on(this.root, 'change', event => this.change(event));
      this.on(this.root, 'input', event => {
        if (event.target.matches('[data-fc-quantity]') && event.target.value !== '' && event.target.validity.valid) this.setQuantity(event.target.value);
      });
      this.on(this.root, 'keydown', event => this.keydown(event));
      this.on(document, 'keydown', event => this.trapDialogFocus(event), { capture: true });
      // Exactly one listener on the native product form. No document-level submit interception.
      if (this.enhancedCart) this.on(this.form, 'submit', event => this.add(event));
      this.on(window, 'popstate', () => {
        const id = new URL(window.location.href).searchParams.get('variant');
        if (this.variants.has(id)) this.selectVariant(id, { updateURL: false });
      });
      for (const dialog of [this.cartDialog, this.zoomDialog].filter(Boolean)) {
        this.on(dialog, 'close', () => {
          unlockScroll(dialog);
          const trigger = this.returnFocus.get(dialog);
          this.returnFocus.delete(dialog);
          if (!this.destroyed && trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
        });
        this.on(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
      }
      this.on(this.reduced, 'change', () => this.respectReducedMotion());
      this.on(this.mobileVideo, 'change', () => this.updateVideoPosters());
      this.on(document, 'visibilitychange', () => { if (document.hidden) this.pauseMedia(); });
      for (const video of all(this.root, 'video')) {
        video.controls = true;
        this.on(video, 'play', () => this.handleVideoPlay(video));
        this.on(video, 'pause', () => this.updateVideoButton(video));
        this.on(video, 'ended', () => this.updateVideoButton(video));
      }
      // Only a verified theme header selector supplied by Liquid is eligible.
      if (this.enhancedCart && this.root.dataset.headerCartSelector) {
        try {
          for (const trigger of all(document, this.root.dataset.headerCartSelector)) {
            this.on(trigger, 'click', event => {
              if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              this.openCart(trigger);
            });
          }
        } catch (_) { /* Invalid optional theme selector must not break native navigation. */ }
      }
    }

    normalizeQuantity(value, variant = this.selected) {
      const min = Math.max(1, safeNumber(variant?.quantityMin ?? variant?.quantityRule?.min, 1));
      const increment = Math.max(1, safeNumber(variant?.quantityIncrement ?? variant?.quantityRule?.increment, 1));
      const rawMax = variant?.quantityMax ?? variant?.quantityRule?.max;
      const max = rawMax == null || rawMax === '' ? Infinity : Math.max(min, safeNumber(rawMax, min));
      const numeric = safeNumber(value, min);
      return Math.min(min + Math.floor((max - min) / increment) * increment,
        min + Math.floor((Math.max(min, numeric) - min) / increment) * increment);
    }
    setQuantity(value) {
      this.quantity = this.normalizeQuantity(value);
      for (const input of all(this.root, '[data-fc-quantity]')) input.value = String(this.quantity);
      for (const button of all(this.root, '[data-fc-quantity-step]')) {
        const step = Number(button.dataset.fcQuantityStep || button.value);
        const next = this.normalizeQuantity(this.quantity + step * Math.max(1, safeNumber(this.selected?.quantityIncrement, 1)));
        button.disabled = this.busy || next === this.quantity;
      }
    }
    selectVariant(id, { announce = true, updateURL = true } = {}) {
      const variant = this.variants.get(String(id));
      if (!variant) return;
      this.selected = variant;
      this.root.dataset.selectedVariant = String(variant.id);
      for (const input of all(this.root, '[data-fc-variant-select]')) input.value = String(variant.id);
      for (const radio of all(this.root, '[data-fc-variant-radio]')) radio.checked = radio.value === String(variant.id);
      for (const input of all(this.form, '[name="id"]')) {
        if (input.type === 'radio') input.checked = input.value === String(variant.id);
        else input.value = String(variant.id);
      }
      for (const input of all(this.root, '[data-fc-quantity]')) {
        input.min = String(Math.max(1, safeNumber(variant.quantityMin, 1)));
        input.step = String(Math.max(1, safeNumber(variant.quantityIncrement, 1)));
        if (variant.quantityMax != null && variant.quantityMax !== '') input.max = String(variant.quantityMax);
        else input.removeAttribute('max');
      }
      this.setQuantity(this.quantity);
      text(this.root, '[data-fc-price]', variant.priceFormatted);
      text(this.root, '[data-fc-compare-price]', variant.compareAtPriceFormatted);
      all(this.root, '[data-fc-compare-price]').forEach(node => { node.hidden = !variant.compareAtPriceFormatted; });
      text(this.root, '[data-fc-sku]', variant.sku);
      text(this.root, '[data-fc-dimensions]', variant.dimensions);
      text(this.root, '[data-fc-net-weight]', variant.netWeightFormatted || variant.netWeight);
      text(this.root, '[data-fc-selected-title]', variant.title);
      text(this.root, '[data-fc-availability]', variant.available ? (this.root.dataset.availableLabel || 'Available') : (this.root.dataset.soldOutLabel || 'Sold out'));
      this.updateBuyButtons();
      if (variant.featuredMediaId != null) this.selectMedia(String(variant.featuredMediaId), false);
      if (updateURL) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        // Keep theme preview, alternate view, locale, campaign parameters and hash intact.
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
      this.setError('');
      if (announce) this.announce(`${variant.title || 'Selected option'}, ${variant.priceFormatted || ''}. ${variant.available ? 'Available.' : 'Sold out.'}`);
    }
    updateBuyButtons() {
      for (const button of all(this.root, '[data-fc-add-button]')) {
        button.disabled = this.busy || !this.selected.available;
        button.setAttribute('aria-busy', String(this.busy));
        const label = button.querySelector('[data-fc-add-label]') || button;
        label.textContent = this.busy ? (this.root.dataset.addingLabel || 'Please wait…') : this.selected.available ? (button.dataset.label || this.root.dataset.addLabel || 'Add to bag') : (this.root.dataset.soldOutLabel || 'Sold out');
      }
    }
    announce(message) { text(this.root, '[data-fc-live]', message); }
    setError(message, cart = false) {
      const selector = cart ? '[data-fc-cart-error]' : '[data-fc-error]';
      for (const node of all(this.root, selector)) { node.textContent = message; node.hidden = !message; }
      if (message) this.announce(message);
    }
    setBusy(busy) {
      if (busy) this.cartFocusBeforeBusy = this.captureCartFocus();
      this.busy = busy;
      this.root.toggleAttribute('data-fc-cart-busy', busy);
      this.form.setAttribute('aria-busy', String(busy));
      this.cartContent?.setAttribute('aria-busy', String(busy));
      const controls = all(this.root, '[data-fc-cart-quantity],[data-fc-cart-remove],[data-fc-cart-step],[data-fc-cart-checkout],[data-fc-variant-select],[data-fc-variant-radio],[data-fc-quantity],[data-fc-quantity-step],[data-fc-cart-retry]');
      if (busy) controls.forEach(control => { this.disabledBeforeBusy.set(control, control.disabled); control.disabled = true; });
      else {
        this.disabledBeforeBusy.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
        this.disabledBeforeBusy.clear();
      }
      this.updateBuyButtons();
      this.setQuantity(this.quantity);
    }
    run(task) {
      if (this.busy || this.destroyed) return Promise.resolve();
      this.setBusy(true);
      const work = runtime.queue.catch(() => {}).then(async () => {
        if (this.destroyed) return;
        try { await task(); }
        catch (error) { if (!this.destroyed) this.setError(error.message || 'The cart could not be loaded.', true); }
        finally { if (!this.destroyed) this.setBusy(false); }
      });
      runtime.queue = work.catch(() => {});
      return work;
    }

    sectionContext() {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.delete('section_id');
      return url.pathname + url.search;
    }
    async request(url, options = {}, responseType = 'json') {
      const controller = new AbortController();
      const abort = () => controller.abort();
      this.abort.signal.addEventListener('abort', abort, { once: true });
      const timeout = window.setTimeout(abort, 20000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        let data;
        try { data = responseType === 'text' ? await response.text() : await response.json(); }
        catch (_) {
          const error = new Error('The server response could not be verified.');
          error.diagnostic = { reason: 'response-parse', status: response.status, contentType: response.headers.get('content-type'), redirected: response.redirected, responseType };
          throw error;
        }
        if (!response.ok) {
          const error = new Error(errorMessage(data));
          error.serverResponse = true;
          error.status = response.status;
          error.diagnostic = { reason: 'http-error', status: response.status, contentType: response.headers.get('content-type'), redirected: response.redirected, responseType };
          throw error;
        }
        return data;
      } finally {
        window.clearTimeout(timeout);
        this.abort.signal.removeEventListener('abort', abort);
      }
    }
    warnCartRead(stage, error, details = {}) {
      // Diagnostics intentionally exclude URLs, bodies, cart data, customer data and server messages.
      const diagnostic = { stage, ...(error?.diagnostic || { reason: error?.name || 'invalid-section' }), ...details };
      this.root.dataset.fcCartDiagnostic = stage;
      this.root.dataset.fcCartDiagnosticDetails = JSON.stringify(diagnostic);
      console.warn('[FCPDP] Cart refresh issue', diagnostic);
    }
    async fetchSection() {
      const url = new URL(this.sectionContext(), window.location.origin);
      // Shopify's documented single-section API returns HTML. On product URLs,
      // Accept: application/json can negotiate product JSON instead of section JSON.
      url.searchParams.set('section_id', this.sectionId);
      let html;
      try { html = await this.request(url.pathname + url.search, { headers: { Accept: 'text/html' } }, 'text'); }
      catch (error) { this.warnCartRead('section-read', error); throw error; }
      if (typeof html !== 'string' || !html.trim()) this.warnCartRead('section-payload', null, { sectionReturned: typeof html, empty: !html });
      return html;
    }
    captureCartFocus() {
      const active = document.activeElement;
      const row = active?.closest?.('[data-fc-cart-line]');
      if (!row || !this.cartContent?.contains(row)) return null;
      return { key: row.dataset.lineKey, index: all(this.cartContent, '[data-fc-cart-line]').indexOf(row), quantity: active.matches('[data-fc-cart-quantity]'), remove: active.matches('[data-fc-cart-remove]') };
    }
    renderSection(html) {
      if (typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('[data-fc-cart-section-content]');
      if (!source || !this.cartContent) {
        this.warnCartRead('section-marker', null, { markerFound: Boolean(source), targetFound: Boolean(this.cartContent) });
        return false;
      }
      const focus = this.cartFocusBeforeBusy || this.captureCartFocus();
      this.cartFocusBeforeBusy = null;
      // Only Shopify-rendered section nodes enter the cart. Never build line HTML from JSON.
      source.querySelectorAll('script').forEach(script => script.remove());
      this.cartContent.replaceChildren(document.importNode(source, true));
      this.cartReady = true;
      this.root.removeAttribute('data-fc-cart-stale');
      if (this.busy) all(this.cartContent, '[data-fc-cart-quantity],[data-fc-cart-remove],[data-fc-cart-step],[data-fc-cart-checkout]').forEach(control => {
        this.disabledBeforeBusy.set(control, control.disabled);
        control.disabled = true;
      });
      if (focus && this.cartDialog?.open) {
        const rows = all(this.cartContent, '[data-fc-cart-line]');
        const row = rows.find(item => item.dataset.lineKey === focus.key) || rows[Math.min(focus.index, rows.length - 1)];
        this.pendingCartFocus = row?.querySelector(focus.remove ? '[data-fc-cart-remove]' : '[data-fc-cart-quantity]') || this.root.querySelector('[data-fc-cart-close]');
      }
      return true;
    }
    updateCartCount(cart) {
      if (!Number.isSafeInteger(cart?.item_count) || !Array.isArray(cart.items)) throw new Error('The current cart could not be verified.');
      this.cart = cart;
      text(this.root, '[data-fc-cart-count]', cart.item_count);
      if (this.root.dataset.headerCartCountSelector) {
        try { text(document, this.root.dataset.headerCartCountSelector, cart.item_count); } catch (_) { /* Optional theme integration. */ }
      }
      if (this.root.dataset.headerCartSelector) {
        try { all(document, this.root.dataset.headerCartSelector).forEach(link => {
          link.setAttribute('aria-label', `Bag, ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`);
          let count = link.querySelector('.fc-header__count');
          if (!count && cart.item_count > 0) { count = document.createElement('span'); count.className = 'fc-header__count'; link.append(count); }
          if (count) { count.textContent = String(cart.item_count); count.hidden = cart.item_count === 0; }
        }); } catch (_) { /* Optional theme integration. */ }
      }
    }
    async refreshCart({ response = null } = {}) {
      let cart;
      try {
        cart = Array.isArray(response?.items) && Number.isSafeInteger(response?.item_count)
          ? response : await this.request(this.urls.cart, { headers: { Accept: 'application/json' } });
        this.updateCartCount(cart);
      } catch (error) { this.warnCartRead('cart-read', error); throw error; }
      let rendered = this.renderSection(response?.sections?.[this.sectionId]);
      if (!rendered) rendered = this.renderSection(await this.fetchSection());
      if (!rendered) throw new Error('Your cart was received, but the bag display could not be refreshed. Open the full cart to review it.');
      delete this.root.dataset.fcCartDiagnostic;
      delete this.root.dataset.fcCartDiagnosticDetails;
      return cart;
    }
    markCartStale() {
      this.cartReady = false;
      this.root.setAttribute('data-fc-cart-stale', '');
      // Stale line keys/totals must not be offered as current or editable.
      if (this.cartContent) this.cartContent.hidden = true;
    }
    restoreCart() { if (this.cartContent) this.cartContent.hidden = false; }
    async refreshProductData() {
      if (!this.root.dataset.sectionId) return;
      try {
        const url = new URL(this.sectionContext(), window.location.origin);
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        const html = await this.request(url.pathname + url.search, { headers: { Accept: 'text/html' } }, 'text');
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const node = parsed.querySelector('[data-fc-pdp] [data-fc-product-data]');
        if (!node) return;
        const product = JSON.parse(node.textContent);
        if (String(product.id) !== String(this.product.id) || !Array.isArray(product.variants)) return;
        this.product = product;
        this.variants = new Map(product.variants.map(variant => [String(variant.id), variant]));
        this.selectVariant(this.selected.id, { announce: false, updateURL: false });
      } catch (_) { /* Keep the last verified variant snapshot; never infer sold out from an error. */ }
    }
    async recoverMutation(error, action, trigger) {
      if (this.destroyed) return;
      let verified = false;
      if (error.serverResponse && [400, 404, 422].includes(error.status)) await this.refreshProductData();
      try { await this.refreshCart(); this.restoreCart(); verified = true; }
      catch (_) { this.markCartStale(); }
      const lead = error.serverResponse ? error.message : `The connection was interrupted while ${action === 'add' ? 'adding the item' : 'updating your bag'}.`;
      const message = `${lead} ${verified ? 'Your current bag has been refreshed. Review it before trying again.' : 'The result could not be confirmed. Open the full cart before trying again.'}`;
      this.setError(message, false);
      this.setError(message, true);
      this.showDialog(this.cartDialog, trigger);
    }
    add(event) {
      if (!this.enhancedCart || event.defaultPrevented) return;
      event.preventDefault();
      if (this.busy || !this.selected.available || !this.form.reportValidity()) return;
      this.setQuantity(this.root.querySelector('[data-fc-quantity]')?.value || this.quantity);
      const body = new FormData(this.form);
      body.set('id', String(this.selected.id));
      body.set('quantity', String(this.quantity));
      body.set('sections', this.sectionId);
      body.set('sections_url', this.sectionContext());
      const trigger = event.submitter || this.root.querySelector('[data-fc-add-button]');
      const variant = this.selected;
      const quantity = this.quantity;
      this.run(async () => {
        this.setError(''); this.setError('', true);
        let response;
        try { response = await this.request(this.urls.add, { method: 'POST', headers: { Accept: 'application/json' }, body }); }
        catch (error) { await this.recoverMutation(error, 'add', trigger); return; }
        try {
          await this.refreshCart({ response });
          this.restoreCart();
          this.announce(`${quantity} × ${variant.title || this.product.title || 'item'} added to your bag.`);
        } catch (_) {
          this.markCartStale();
          this.setError('The item was added, but the bag display could not be refreshed. Open the full cart to review it. Do not add it again.', true);
        }
        this.showDialog(this.cartDialog, trigger);
      });
    }
    changeLine(key, quantity, trigger) {
      if (!key || !Number.isSafeInteger(quantity) || quantity < 0 || this.busy) return;
      this.run(async () => {
        this.setError('', true);
        let response;
        try {
          response = await this.request(this.urls.change, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: String(key), quantity, sections: [this.sectionId], sections_url: this.sectionContext() }) });
        } catch (error) { await this.recoverMutation(error, 'change', trigger); return; }
        try {
          const cart = await this.refreshCart({ response });
          this.restoreCart();
          const item = cart.items.find(line => line.key === key);
          this.announce(quantity === 0 ? 'Item removed. Your bag has been updated.' : item && item.quantity !== quantity ? `Your bag has been updated to the available quantity, ${item.quantity}.` : 'Your bag quantity and totals have been updated.');
        } catch (_) {
          this.markCartStale();
          this.setError('Your cart request completed, but the bag display could not be refreshed. Open the full cart to review the current quantities and totals.', true);
        }
      }).then(() => {
        if (!this.destroyed && this.cartDialog?.open && this.pendingCartFocus?.isConnected) this.pendingCartFocus.focus({ preventScroll: true });
        this.pendingCartFocus = null;
      });
    }
    openCart(trigger) {
      if (!this.enhancedCart) return false;
      this.showDialog(this.cartDialog, trigger);
      if (this.busy) return true;
      this.run(async () => {
        this.setError('', true);
        try { await this.refreshCart(); this.restoreCart(); }
        catch (_) { this.markCartStale(); this.setError('Your bag could not be loaded. Try refreshing it or open the full cart.', true); }
      });
      return true;
    }
    trapDialogFocus(event) {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const dialog = [this.zoomDialog, this.cartDialog].find(item => item?.open);
      if (!dialog) return;
      const focusable = all(dialog, 'a[href],area[href],button,input,select,textarea,iframe,object,embed,[tabindex],[contenteditable="true"],summary').filter(node =>
        !node.disabled && !node.matches(':disabled') && node.tabIndex >= 0 && !node.closest('[hidden],[inert]') &&
        node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (!dialog.contains(active) || !focusable.includes(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault(); first.focus();
      }
    }
    showDialog(dialog, trigger) {
      if (!dialog?.showModal || dialog.open || this.destroyed) return;
      this.pauseMedia();
      this.returnFocus.set(dialog, trigger || document.activeElement);
      dialog.showModal();
      lockScroll(dialog);
    }

    click(event) {
      const button = event.target.closest?.('button,a,[data-fc-cart-open]');
      if (!button || !this.root.contains(button)) return;
      if (button.matches('[data-fc-cart-open]')) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (this.openCart(button)) event.preventDefault();
      } else if (button.matches('[data-fc-cart-close]')) this.cartDialog?.close();
      else if (button.matches('[data-fc-cart-retry]')) { event.preventDefault(); this.openCart(button); }
      else if (button.matches('[data-fc-quantity-step]')) {
        event.preventDefault();
        if (!this.busy) this.setQuantity(this.quantity + Number(button.dataset.fcQuantityStep || button.value) * Math.max(1, safeNumber(this.selected.quantityIncrement, 1)));
      } else if (button.matches('[data-fc-cart-remove],[data-fc-cart-step]')) {
        if (!this.enhancedCart) return;
        event.preventDefault();
        const row = button.closest('[data-fc-cart-line]');
        const input = row?.querySelector('[data-fc-cart-quantity]');
        const quantity = button.matches('[data-fc-cart-remove]') ? 0 : Math.max(0, safeNumber(input?.value, 0) + Number(button.dataset.fcCartStep || button.value));
        this.changeLine(row?.dataset.lineKey, quantity, button);
      } else if (button.matches('[data-fc-gallery-thumb]')) {
        event.preventDefault(); this.selectMedia(button.dataset.mediaId);
      } else if (button.matches('[data-fc-gallery-prev],[data-fc-gallery-next]')) {
        event.preventDefault();
        const current = this.media.findIndex(panel => !panel.hidden);
        this.selectMedia(this.media[(current + (button.matches('[data-fc-gallery-next]') ? 1 : -1) + this.media.length) % this.media.length]?.dataset.mediaId);
      } else if (button.matches('[data-fc-zoom-open]')) {
        if (this.openZoom(button)) event.preventDefault();
      } else if (button.matches('[data-fc-zoom-close]')) this.zoomDialog?.close();
      else if (button.matches('[data-fc-video-toggle]')) { event.preventDefault(); this.toggleVideo(button); }
    }
    change(event) {
      const input = event.target;
      if (input.matches('[data-fc-variant-select],[data-fc-variant-radio]')) {
        if (!this.busy) this.selectVariant(input.value);
      } else if (input.matches('[data-fc-quantity]')) this.setQuantity(input.value);
      else if (input.matches('[data-fc-cart-quantity]') && this.enhancedCart) {
        if (!input.reportValidity()) return;
        const quantity = safeNumber(input.value, -1);
        this.changeLine(input.closest('[data-fc-cart-line]')?.dataset.lineKey, quantity, input);
      }
    }
    keydown(event) {
      const thumb = event.target.closest?.('[data-fc-gallery-thumb]');
      if (!thumb || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = this.thumbs.indexOf(thumb);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.thumbs.length - 1 : (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + this.thumbs.length) % this.thumbs.length;
      this.selectMedia(this.thumbs[next].dataset.mediaId);
      this.thumbs[next].focus({ preventScroll: true });
    }
    selectMedia(id, announce = true) {
      if (!id || !this.media.some(panel => panel.dataset.mediaId === String(id))) return;
      let index = 0;
      this.media.forEach((panel, i) => {
        const active = panel.dataset.mediaId === String(id);
        panel.hidden = !active;
        panel.toggleAttribute('data-active', active);
        if (active) {
          index = i;
          all(panel, 'iframe[data-fc-video-frame]').forEach(frame => { if (frame.dataset.fcPausedSrc && !frame.getAttribute('src')) frame.setAttribute('src', frame.dataset.fcPausedSrc); });
        } else this.pauseMedia(panel);
      });
      this.thumbs.forEach(thumb => {
        const active = thumb.dataset.mediaId === String(id);
        thumb.setAttribute('aria-current', String(active));
        thumb.setAttribute('aria-pressed', String(active));
        if (thumb.getAttribute('role') === 'tab') thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
      });
      text(this.root, '[data-fc-gallery-counter]', `${index + 1} / ${this.media.length}`);
      if (announce) text(this.root, '[data-fc-gallery-live]', `Showing media ${index + 1} of ${this.media.length}.`);
    }
    pauseMedia(scope = this.root) {
      all(scope, 'video').forEach(video => video.pause());
      all(scope, 'model-viewer').forEach(model => model.pause?.());
      all(scope, 'iframe[data-fc-video-frame]').forEach(frame => {
        if (frame.getAttribute('src')) { frame.dataset.fcPausedSrc = frame.getAttribute('src'); frame.removeAttribute('src'); }
      });
    }
    respectReducedMotion() {
      if (!this.reduced.matches) return;
      for (const video of all(this.root, 'video')) { video.autoplay = false; video.removeAttribute('autoplay'); video.pause(); }
    }
    updateVideoPosters() {
      for (const video of all(this.root, 'video[data-fc-desktop-poster],video[data-fc-mobile-poster]')) {
        const desktop = video.dataset.fcDesktopPoster;
        const mobile = video.dataset.fcMobilePoster;
        const poster = this.mobileVideo.matches ? (mobile || desktop) : (desktop || mobile);
        // Updating only the poster preserves source, playback state and currentTime.
        if (poster && video.getAttribute('poster') !== poster) video.setAttribute('poster', poster);
      }
    }
    handleVideoPlay(video) {
      for (const other of all(this.root, 'video')) if (other !== video && !other.paused) other.pause();
      this.updateVideoButton(video);
    }
    updateVideoButton(video) {
      all(this.root, '[data-fc-video-toggle]').filter(button => button.dataset.videoId === video.id).forEach(button => {
        button.setAttribute('aria-pressed', String(!video.paused));
        const label = button.querySelector('[data-fc-video-label]') || button;
        label.textContent = video.paused ? 'Play video' : 'Pause video';
      });
    }
    toggleVideo(button) {
      const video = all(this.root, 'video').find(item => item.id === button.dataset.videoId);
      if (!video) return;
      if (!video.paused) { video.pause(); return; }
      video.play().catch(() => { text(this.root, '[data-fc-gallery-live]', 'The video could not start. Use the video controls to try again.'); });
    }
    openZoom(button) {
      const panel = button.closest('[data-fc-gallery-media]') || this.media.find(item => !item.hidden);
      const image = panel?.querySelector('img');
      const target = this.zoomDialog?.querySelector('[data-fc-zoom-image]');
      const src = button.dataset.fullSrc || button.dataset.imageUrl || image?.dataset.fullSrc || image?.currentSrc || image?.src;
      if (!target || !src || !this.zoomDialog?.showModal) return false;
      target.src = src;
      target.alt = button.dataset.fullAlt || image?.alt || 'Product detail';
      text(this.zoomDialog, '[data-fc-zoom-label]', target.alt);
      this.showDialog(this.zoomDialog, button);
      return true;
    }
    observeRail() {
      const rail = this.root.querySelector('.rail');
      if (!rail || !('ResizeObserver' in window)) return;
      this.previousRailHeight = document.body.style.getPropertyValue('--fc-pdp-rail-height');
      this.railObserver = new ResizeObserver(() => {
        if (!this.destroyed) document.body.style.setProperty('--fc-pdp-rail-height', `${Math.ceil(rail.getBoundingClientRect().height)}px`);
      });
      this.railObserver.observe(rail);
    }
    destroy() {
      this.destroyed = true;
      if (this.busy) this.setBusy(false);
      this.railObserver?.disconnect();
      if (this.previousRailHeight !== undefined) {
        if (this.previousRailHeight) document.body.style.setProperty('--fc-pdp-rail-height', this.previousRailHeight);
        else document.body.style.removeProperty('--fc-pdp-rail-height');
      }
      this.pauseMedia();
      for (const dialog of [this.cartDialog, this.zoomDialog].filter(Boolean)) { if (dialog.open) dialog.close(); unlockScroll(dialog); }
      this.abort.abort();
      this.root.removeAttribute('data-fc-enhanced');
      runtime.instances.delete(this.root);
    }
  }

  runtime.mount = scope => {
    const roots = [...(scope.matches?.('[data-fc-pdp]') ? [scope] : []), ...all(scope, '[data-fc-pdp]')];
    for (const root of roots) {
      if (runtime.instances.has(root)) continue;
      try { runtime.instances.set(root, new PDPController(root)); }
      catch (_) { /* Malformed enhancement data leaves the native Shopify form untouched. */ }
    }
  };
  window[KEY] = runtime;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => runtime.mount(document), { once: true });
  else runtime.mount(document);
  document.addEventListener('shopify:section:load', event => runtime.mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    runtime.instances.forEach((controller, root) => { if (event.target === root || event.target.contains(root)) controller.destroy(); });
  });
})();
